import { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControl, FormControlLabel, FormLabel, IconButton, InputLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';

import { ICustomer } from "../../types/customer";
import { IMaterialPrice } from '../../types/material';
import { CustomerService } from '../../services/customerService';
import { MaterialPriceService } from '../../services/materialPriceService';

import { useListContext } from '../../contexts/listContext';

import NumberField from '../shared/numberField';
import Spinner from '../spinner';
import Json from '../json';

import '../../styles/materialPriceUpdateForm.scss';

export interface IMaterialPriceUpdateForm {
	id: string;
}

const MaterialPriceUpdateForm: React.FunctionComponent<IMaterialPriceUpdateForm> = ({ id }) => {
	const navigate = useNavigate();
	const listContext = useListContext();
	const [isLoadingMaterial, setIsLoadingMaterial] = useState(false);
	const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [material, setMaterial] = useState<IMaterialPrice | undefined>();
	const [customer, setCustomer] = useState<ICustomer | undefined>();
	const [terminationDate, setTerminationDate] = useState(dayjs());
	const [newPrice, setNewPrice] = useState<number>(0.00);
	const [isGrossWeightMaterial, setIsGrossWeightMaterial] = useState<boolean>(false);

	const [terminateWithoutUpdating, setTerminateWithoutUpdating] = useState<boolean>(false);
	const [earliestTerminationDate, setEarliestTerminationDate] = useState<Dayjs>(dayjs());

	const brandsWithCustomMaterialIds: string[] = [
		"Michelin"
	];

	useEffect(() => {
		getMaterial();
		setEarliestTerminationDate(getEarliestTerminationDate());
	}, [id])

	useEffect(() => {
		if (material) {
			getCustomer();
		}
	}, [material])

	const getMaterial = async () => {
		if (id) {
			setIsLoadingMaterial(true);
			MaterialPriceService.getMaterialPrice(id)
				.then((response) => {
					console.log('MaterialService.getMaterial: SUCCESS', response);
					setMaterial(MaterialPriceService.mapMaterialPrice(response.data));
					const material = MaterialPriceService.mapMaterialPrice(response.data);
					setNewPrice(material.priceByContainer == true ? (material.containerPrice ?? 0) : (material.price ?? 0));
					setIsGrossWeightMaterial(material.isGrossWeightMaterial ?? false);
				})
				.catch((error) => {
					console.log('MaterialService.getMaterial: ERROR', error)
				})
				.finally(() => {
					setIsLoadingMaterial(false);
				});
		}
	}

	const getCustomer = async () => {
		if (id) {
			setIsLoadingCustomer(true);
			CustomerService.getCustomer(material.customerId.toString())
				.then((response) => {
					console.log('CustomerService.getCustomer: SUCCESS', response);
					setCustomer(CustomerService.mapCustomer(response.data));
				})
				.catch((error) => {
					console.log('CustomerService.getCustomer: ERROR', error)
				})
				.finally(() => {
					setIsLoadingCustomer(false);
				});
		}
	}

	const handleSubmitUpdate = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (compareDates(terminationDate, earliestTerminationDate)) {
			setErrorMessage(undefined);
			setIsSaving(true);

			MaterialPriceService.updateMaterialPrice(material?.id, newPrice, terminationDate.toDate(), isGrossWeightMaterial)
				.then((response) => {
					console.log('MaterialService.updateMaterial: SUCCESS', response);
					navigate("/materialprice");
				})
				.catch((error) => {
					console.log('MaterialService.updateMaterial: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
		else {
			setErrorMessage(`Last Date for Old Price is required and it must be on or after ${earliestTerminationDate.format('LL')}.`);
		}
	}

	const handleSubmitTerminate = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (compareDates(terminationDate, earliestTerminationDate)) {
			setErrorMessage(undefined);
			setIsSaving(true);

			MaterialPriceService.terminateMaterialPrice(material?.id, terminationDate.toDate())
				.then((response) => {
					console.log('MaterialService.terminateMaterial: SUCCESS', response);
					navigate("/materialprice");
				})
				.catch((error) => {
					console.log('MaterialService.terminateMaterial: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
		else {
			setErrorMessage(`Last Date for Old Price is required and it must be on or after ${earliestTerminationDate.format('LL')}.`);
		}
	}

	const compareDates = (firstDate: Dayjs, secondDate: Dayjs) => {
		const firstDateAsInt = moment(firstDate.toString()).format("YYYYMMDD");
		const secondDateAsInt = moment(secondDate.toString()).format("YYYYMMDD");

		if (firstDateAsInt >= secondDateAsInt) {
			return true;
		}

		return false;
	}

	const getEarliestTerminationDate = (): Dayjs => {
		// The earliest termination date is always the last day of the 2nd previous month (the 1st of the previous month minus 1 day)
		return dayjs().subtract(1, 'month').startOf('month').subtract(1, 'day');
	}

	const customerBrandHasCustomMaterialIds = (): boolean => {
		return brandsWithCustomMaterialIds?.map(c => c.toLowerCase())?.includes(customer?.details?.brandName?.toLowerCase()) ?? false;
	}

	return (
		<section className="hwi-material-price-update-form">
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Spinner dependency={isLoadingMaterial || isLoadingCustomer} text='Loading material price information...'>

					<div className="form-selector">
						<FormControl>
							<FormLabel id="demo-radio-buttons-group-label">Would you like to update this price, or just set an end date after which it will no longer be available to use?</FormLabel>
							<RadioGroup
								aria-labelledby="demo-radio-buttons-group-label"
								value={terminateWithoutUpdating}
								name="radio-buttons-group"
								onChange={(event) => setTerminateWithoutUpdating(event.target.value == "true" ? true : false)}
							>
								<FormControlLabel value={false} control={<Radio />} label="Terminate the existing price and create a new price to replace it." />
								<FormControlLabel value={true} control={<Radio />} label="Just terminate the existing price." />
							</RadioGroup>
						</FormControl>
					</div>
					<div className="new-price">
						<h3>New Price Information</h3>
						{errorMessage &&
							<Alert className="alert-error"
								severity="error"
								action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
								{errorMessage}
							</Alert>
						}
						{terminateWithoutUpdating ? (
							<Form className="form-terminate" onSubmit={(event) => handleSubmitTerminate(event)}>
								<div className="row controls">
									<div className="row">
										<InputLabel>Last Date for Old Price *</InputLabel>
										<DatePicker
											className="end-date-field"
											value={dayjs(terminationDate)}
											minDate={earliestTerminationDate}
											onChange={(value) => setTerminationDate(value)}
											slotProps={{
												textField: {
													required: true,
												},
											}}
										/>
									</div>
									<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
								</div>
							</Form>
						) : (
							<Form className="form-update" onSubmit={(event) => handleSubmitUpdate(event)}>
								<div className="row">
									<InputLabel>Last Date for Old Price *</InputLabel>
									<DatePicker
										className="end-date-field"
										value={dayjs(terminationDate)}
										minDate={earliestTerminationDate}
										onChange={(value) => setTerminationDate(value)}
										slotProps={{
											textField: {
												required: true,
											},
										}}
									/>
								</div>
								<div className="row">
									{(material?.priceByContainer != true) && <InputLabel>New Price Per Unit ({material?.uomName})*</InputLabel>}
									{(material?.priceByContainer == true) && <InputLabel>New Price Per Container ({listContext.containers.find(c => c.id == material?.containerId)?.name ?? `Unknown (ID ${material?.containerId})`}) *</InputLabel>}
									<NumberField className="price-field"
										name="newPrice"
										precision={5}
										required
										onFocus={(event) => event.target.select()}
										onBlur={(event) => { setNewPrice(+event.target.value) }}
										value={newPrice}
									></NumberField>
								</div>
								<div className="row">
									<FormControlLabel
										className="gross-weight-field"
										control={
											<Checkbox
												size='small'
												checked={isGrossWeightMaterial}
												onChange={() => setIsGrossWeightMaterial(!isGrossWeightMaterial)}
											></Checkbox>}
										label="This is a gross weight material. Tare weight should be included in the cost/credit calculation." />
								</div>
								<div className="row">
									<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
								</div>
							</Form>
						)}
					</div>


					{material &&
						<div className="current-price">
							<h3>Current Price Information (Read Only)</h3>
							<div className="row">
								<InputLabel>Customer</InputLabel>
								<TextField className="customer-field"
									required
									name="customerName"
									disabled
									value={material.customerName ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Vendor</InputLabel>
								<TextField className="vendor-field"
									required
									name="vendorName"
									disabled
									value={material.vendorName ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Material</InputLabel>
								<TextField className="material-field"
									required
									name="materialName"
									disabled
									value={material.materialName ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Customer Code</InputLabel>
								<TextField className="category-field"
									required
									name="categoryName"
									disabled
									value={material.customerCategoryName ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Profile</InputLabel>
								<TextField className="profile-field"
									required
									name="profile"
									disabled
									value={material.profile ?? ""}
								></TextField>
							</div>
							{customerBrandHasCustomMaterialIds() &&
								<div className="row">
									<InputLabel>{customer?.details?.brandName ?? "Customer"} Material ID</InputLabel>
									<TextField className="customer-material-id-field"
										required
										name="customer-material-id"
										disabled
										value={material.customerMaterialId ?? "N/A"}
									></TextField>
								</div>
							}
							{material.vendorUsesExternalPriceIds &&
								<>
									<div className="row">
										<InputLabel>Vendor Material ID</InputLabel>
										<TextField className="vendor-material-id-field"
											required
											name="vendor-material-id"
											disabled
											value={material.externalPriceId ?? ""}
										></TextField>
									</div>
									<div className="row">
										<InputLabel>Vendor Material Name</InputLabel>
										<TextField className="vendor-material-name-field"
											required
											name="vendor-material-name"
											disabled
											value={material.externalMaterialName ?? ""}
										></TextField>
									</div>
								</>
							}
							<div className="row">
								<InputLabel>Price By</InputLabel>
								<TextField className="price-by-field"
									required
									name="priceBy"
									disabled
									value={material.priceByContainer == true ? "Container" : "Weight"}
								></TextField>
							</div>
							{material.priceByContainer == true &&
								<>
									<div className="row">
										<InputLabel>Container</InputLabel>
										<TextField className="container-field"
											required
											name="container"
											disabled
											value={listContext.containers.find(c => c.id == material.containerId)?.name ?? `Unknown (ID ${material.containerId})`}
										></TextField>
									</div>
									<div className="row">
										<InputLabel>Container Price</InputLabel>
										<TextField className="container-price-field"
											required
											name="container-price"
											disabled
											value={material.containerPrice ?? "N/A"}
										></TextField>
									</div>
								</>
							}
							<div className="row">
								<InputLabel>Waste Type</InputLabel>
								<TextField className="waste-field"
									required
									name="wasteType"
									disabled
									value={material.wasteType ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Recycle Method</InputLabel>
								<TextField className="method-field"
									required
									name="recycleMethod"
									disabled
									value={material.recycleMethod ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Unit of Measure</InputLabel>
								<TextField className="uom-field"
									required
									name="uomName"
									disabled
									value={material.uomName ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Price Per Unit *</InputLabel>
								<TextField className="uom-field"
									required
									name="uomName"
									disabled
									value={material.price ?? ""}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Is this a gross weight material?</InputLabel>
								<TextField className="gross-weight-field"
									required
									name="grossWeightMaterial"
									disabled
									value={material.isGrossWeightMaterial ? "Yes" : "No"}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Effective Date</InputLabel>
								<TextField className="end-date-field"
									required
									name="endDate"
									disabled
									value={moment(material.startDate).format("MM/DD/YYYY")}
								></TextField>
							</div>
						</div>
					}
				</Spinner>
				{/* <Json data={material}></Json> */}
			</LocalizationProvider>
		</section>
	)
}

export default MaterialPriceUpdateForm;